import React, { useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import "./ModelRankList.css";

/**
 * Single-column drag-to-rank list, autosaved on every drop. The caller owns
 * the items array: onReorder(reorderedItems) must set its state to the new
 * order SYNCHRONOUSLY — before its first await — or the dropped row visibly
 * snaps back to its old position for a frame, then jumps again once the
 * state lands. After that optimistic update the caller persists and, on
 * failure, reverts its own state. Errors thrown by onReorder are caught and
 * logged here (so a rejection cannot escape as an unhandled promise
 * rejection), but the caller still owns revert-on-failure and any user-facing
 * error message. While onReorder is in flight, dragging is disabled to avoid
 * interleaved saves.
 */
export default function ModelRankList({ items, getKey, renderItem, onReorder, topBadge = "default" }) {
  const [saving, setSaving] = useState(false);

  const handleDragEnd = async (result) => {
    const { source, destination } = result;
    if (!destination || destination.index === source.index) return;
    const reordered = [...items];
    const [moved] = reordered.splice(source.index, 1);
    reordered.splice(destination.index, 0, moved);
    setSaving(true);
    try {
      await onReorder(reordered);
    } catch (err) {
      console.error("ModelRankList: onReorder failed", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="model-rank-list">
        {(provided) => (
          <ul
            className="model-rank-list"
            ref={provided.innerRef}
            {...provided.droppableProps}
          >
            {items.map((item, index) => (
              <Draggable
                key={String(getKey(item))}
                draggableId={String(getKey(item))}
                index={index}
                isDragDisabled={saving}
              >
                {(dragProvided, snapshot) => (
                  <li
                    ref={dragProvided.innerRef}
                    {...dragProvided.draggableProps}
                    {...dragProvided.dragHandleProps}
                    className={`model-rank-item${snapshot.isDragging ? " dragging" : ""}`}
                  >
                    <span className="model-rank-grip" aria-hidden="true">⠿</span>
                    <span className="model-rank-position">{index + 1}</span>
                    {renderItem(item, index)}
                    {index === 0 && topBadge && (
                      <span className="model-rank-default-badge">{topBadge}</span>
                    )}
                  </li>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </ul>
        )}
      </Droppable>
    </DragDropContext>
  );
}
